import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Table,
  Statistic,
  Spin,
  Modal,
  Input,
  Button,
  notification,
  TableProps,
  Space,
} from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import ContainerLayout from '../../common/containerLayout';
import {
  getTripRequestAnalyticsStats,
  getAnalyticsCompanyBreakdown,
  getAnalyticsUserDetail,
  sendFollowUpEmail,
} from '../../../services/ReportsApi';

dayjs.extend(weekOfYear);

const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface DateRange {
  startDate: Dayjs;
  endDate: Dayjs;
}

interface Stats {
  totalVisits: number;
  totalQuotes: number;
  totalSubmissions: number;
  conversionRate: number;
}

interface CompanyRow {
  adminId: number;
  companyName: string;
  totalVisits: number;
  uniqueUsers: number;
  totalQuotes: number;
  totalSubmissions: number;
  conversionRate: number;
  lastActivity: string;
}

interface UserDetail {
  userId: number;
  userEmail: string;
  userName: string;
  totalVisits: number;
  totalQuotes: number;
  totalSubmissions: number;
  conversionRate: number;
  lastActivity: string;
}

interface FollowUpModalState {
  visible: boolean;
  recipientEmail: string;
  recipientName: string;
  subject: string;
  body: string;
  sending: boolean;
}

const TripRequestAnalyticsView: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: dayjs().startOf('month'),
    endDate: dayjs().endOf('month'),
  });
  const [allCompanies, setAllCompanies] = useState<CompanyRow[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [followUp, setFollowUp] = useState<FollowUpModalState>({
    visible: false,
    recipientEmail: '',
    recipientName: '',
    subject: 'Notamos que no completaste tu reserva',
    body: '',
    sending: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = dateRange.startDate.format('YYYY-MM-DD');
      const endDate = dateRange.endDate.add(1, 'day').format('YYYY-MM-DD');

      const [statsData, companiesData] = await Promise.all([
        getTripRequestAnalyticsStats(startDate, endDate),
        getAnalyticsCompanyBreakdown(startDate, endDate),
      ]);

      setStats(statsData);
      setAllCompanies(companiesData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      notification.error({ message: 'Error al cargar datos de conversión', placement: 'bottom' });
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange: RangePickerProps['onChange'] = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange({ startDate: dates[0], endDate: dates[1] });
    }
  };

  const companyOptions = useMemo(() => {
    const unique = Array.from(
      new Set(allCompanies.map((c) => c.adminId))
    ).map((id) => allCompanies.find((c) => c.adminId === id)) as CompanyRow[];
    return unique.map((c) => ({ value: c.adminId, label: c.companyName }));
  }, [allCompanies]);

  const filteredCompanies = useMemo(() => {
    if (selectedCompanyIds.length === 0) return allCompanies;
    return allCompanies.filter((c) => selectedCompanyIds.includes(c.adminId));
  }, [allCompanies, selectedCompanyIds]);

  const filteredStats = useMemo(() => {
    if (selectedCompanyIds.length === 0 || !allCompanies.length) return stats;
    const filtered = allCompanies.filter((c) => selectedCompanyIds.includes(c.adminId));
    const visits = filtered.reduce((sum, c) => sum + c.totalVisits, 0);
    const quotes = filtered.reduce((sum, c) => sum + c.totalQuotes, 0);
    const submissions = filtered.reduce((sum, c) => sum + c.totalSubmissions, 0);
    const rate = visits > 0 ? Math.round((submissions / visits) * 100 * 100) / 100 : 0;
    return { totalVisits: visits, totalQuotes: quotes, totalSubmissions: submissions, conversionRate: rate };
  }, [stats, allCompanies, selectedCompanyIds]);

  const openFollowUpModal = (user: UserDetail) => {
    setFollowUp({
      visible: true,
      recipientEmail: user.userEmail,
      recipientName: user.userName,
      subject: 'Notamos que no completaste tu reserva',
      body: `Hola ${user.userName},\n\nNotamos que no completaste tu reserva. ¿Podemos ayudarte con algo?\n\nSaludos,\nEquipo de Mowi`,
      sending: false,
    });
  };

  const closeFollowUpModal = () => {
    setFollowUp((prev) => ({ ...prev, visible: false }));
  };

  const handleSendFollowUp = async () => {
    if (!followUp.recipientEmail || !followUp.subject || !followUp.body) {
      notification.warning({ message: 'Todos los campos son requeridos', placement: 'bottom' });
      return;
    }

    setFollowUp((prev) => ({ ...prev, sending: true }));
    try {
      await sendFollowUpEmail(followUp.recipientEmail, followUp.subject, followUp.body);
      notification.success({ message: 'Email enviado correctamente', placement: 'bottom' });
      closeFollowUpModal();
    } catch (error) {
      console.error('Error sending follow-up email:', error);
      notification.error({ message: 'Error al enviar el email', placement: 'bottom' });
    } finally {
      setFollowUp((prev) => ({ ...prev, sending: false }));
    }
  };

  const UserDetailTable: React.FC<{ adminId: number; dateRange: DateRange }> = ({
    adminId,
    dateRange,
  }) => {
    const [userDetails, setUserDetails] = useState<UserDetail[]>([]);
    const [userLoading, setUserLoading] = useState(false);

    useEffect(() => {
      const loadUsers = async () => {
        setUserLoading(true);
        try {
          const start = dateRange.startDate.format('YYYY-MM-DD');
          const end = dateRange.endDate.format('YYYY-MM-DD');
          const users = await getAnalyticsUserDetail(adminId, start, end);
          setUserDetails(users);
        } catch {
          setUserDetails([]);
        } finally {
          setUserLoading(false);
        }
      };
      loadUsers();
    }, [adminId, dateRange]);

    const userColumns = [
      { title: 'Usuario', dataIndex: 'userName', key: 'userName' },
      { title: 'Email', dataIndex: 'userEmail', key: 'userEmail' },
      { title: 'Visitas', dataIndex: 'totalVisits', key: 'totalVisits' },
      { title: 'Cotizaciones', dataIndex: 'totalQuotes', key: 'totalQuotes' },
      { title: 'Reservas', dataIndex: 'totalSubmissions', key: 'totalSubmissions' },
      {
        title: 'Conv.%',
        dataIndex: 'conversionRate',
        key: 'conversionRate',
        render: (rate: number) => `${rate}%`,
      },
      {
        title: 'Acciones',
        key: 'actions',
        render: (_: unknown, user: UserDetail) => (
          <Button
            type="primary"
            size="small"
            onClick={() => openFollowUpModal(user)}
            disabled={user.userEmail === 'Invitado'}
          >
            ✉️ Email
          </Button>
        ),
      },
    ];

    return (
      <Table
        columns={userColumns}
        dataSource={userDetails}
        loading={userLoading}
        rowKey="userId"
        pagination={false}
        size="small"
      />
    );
  };

  const expandedRowRender = (record: CompanyRow) => (
    <UserDetailTable adminId={record.adminId} dateRange={dateRange} />
  );

  const companyColumns: TableProps<CompanyRow>['columns'] = [
    { title: 'Empresa', dataIndex: 'companyName', key: 'companyName' },
    { title: 'Visitas', dataIndex: 'totalVisits', key: 'totalVisits' },
    { title: 'Usuarios', dataIndex: 'uniqueUsers', key: 'uniqueUsers' },
    { title: 'Cotizaciones', dataIndex: 'totalQuotes', key: 'totalQuotes' },
    { title: 'Reservas', dataIndex: 'totalSubmissions', key: 'totalSubmissions' },
    {
      title: 'Conv.%',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      render: (rate: number) => `${rate}%`,
    },
    {
      title: 'Última Actividad',
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      render: (date: string) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'),
    },
  ];

  return (
    <ContainerLayout
      title="Conversión de Viajes Internacionales"
      showNotification={false}
      defaultContainer={false}
    >
      <div style={{ padding: 24 }}>
        <Card>
          <Form form={form} layout="vertical">
            <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
              <Col xs={24} md={8} lg={7}>
                <Form.Item label="Compañía">
                  <Select
                    mode="multiple"
                    showSearch
                    optionFilterProp="label"
                    placeholder="Todas las compañías"
                    style={{ width: '100%' }}
                    value={selectedCompanyIds}
                    onChange={setSelectedCompanyIds}
                    options={companyOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={10} lg={9}>
                <Form.Item label="Rango de Fechas">
                  <RangePicker
                    style={{ width: '100%' }}
                    value={[dateRange.startDate, dateRange.endDate]}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6} lg={4}>
                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={fetchData} loading={loading}>
                      Buscar
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Spin spinning={loading}>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic title="Visitas" value={filteredStats?.totalVisits ?? stats?.totalVisits ?? 0} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic title="Cotizaciones" value={filteredStats?.totalQuotes ?? stats?.totalQuotes ?? 0} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic title="Reservas Completadas" value={filteredStats?.totalSubmissions ?? stats?.totalSubmissions ?? 0} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small">
                  <Statistic
                    title="Tasa de Conversión"
                    value={filteredStats?.conversionRate ?? stats?.conversionRate ?? 0}
                    suffix="%"
                    valueStyle={{ color: (filteredStats?.conversionRate ?? stats?.conversionRate ?? 0) > 20 ? '#3f8600' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            <Table<CompanyRow>
              columns={companyColumns}
              dataSource={filteredCompanies}
              expandable={{
                expandedRowRender,
                rowExpandable: (record) => record.uniqueUsers > 0,
              }}
              loading={loading}
              rowKey="adminId"
              pagination={{ pageSize: 20 }}
              scroll={{ x: 900 }}
            />
          </Spin>
        </Card>
      </div>

      <Modal
        title="Enviar Email de Seguimiento"
        open={followUp.visible}
        onCancel={closeFollowUpModal}
        footer={[
          <Button key="cancel" onClick={closeFollowUpModal}>
            Cancelar
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={followUp.sending}
            onClick={handleSendFollowUp}
          >
            Enviar
          </Button>,
        ]}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="Para">
            <Input value={followUp.recipientEmail} disabled />
          </Form.Item>
          <Form.Item label="Asunto">
            <Input
              value={followUp.subject}
              onChange={(e) => setFollowUp((prev) => ({ ...prev, subject: e.target.value }))}
            />
          </Form.Item>
          <Form.Item label="Mensaje">
            <TextArea
              rows={8}
              value={followUp.body}
              onChange={(e) => setFollowUp((prev) => ({ ...prev, body: e.target.value }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ContainerLayout>
  );
};

export default TripRequestAnalyticsView;
